import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import logoImg from "../../../Images/logo_main_black_resized.png";
import { buildBreakdownSections } from "./FinancialBreakdown";

const fmtCAD = (v: number) => v.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
const n = (v: any) => Number(v) || 0;

const DEFAULT_TERMS = [
  "All bookings require a deposit to confirm the reservation.",
  "Final guest count must be provided 7 days prior to the event.",
  "Cancellation within 14 days may result in partial or full charge.",
  "Any damages to property will be billed to the client.",
  "Overtime charges apply beyond booked hours.",
  "Outside vendors must be approved by management.",
  "Prices are subject to HST (13%).",
  "Durham Convention Centre reserves the right to reassign halls in the event of an emergency.",
];

const SLOT_LABELS: Record<string, string> = {
  AM: "8:00 AM – 3:00 PM", PM: "6:00 PM – 1:00 AM", FULL_DAY: "8:00 AM – 1:00 AM",
};

interface Props { booking: any; }

/* ── Styles ── */
const S = {
  page: { fontFamily: "'Inter', 'Segoe UI', sans-serif", color: "#1a1a1a", fontSize: 11, lineHeight: 1.5, p: 4, maxWidth: 800, mx: "auto", "@media print": { p: 0, maxWidth: "100%" } },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3, pb: 2.5, borderBottom: "2px solid #0d1b2a" },
  sectionTitle: { fontSize: 10, fontWeight: 700, color: "#0d1b2a", textTransform: "uppercase" as const, letterSpacing: "0.08em", mb: 1, mt: 3, pb: 0.5, borderBottom: "1px solid #e5e1db" },
  row: { display: "flex", justifyContent: "space-between", py: "3px" },
  label: { fontSize: 11, color: "#6b7280" },
  value: { fontSize: 11, color: "#1a1a1a", fontWeight: 500 },
  amount: { fontSize: 11, color: "#1a1a1a", fontWeight: 600, fontVariantNumeric: "tabular-nums" as const, textAlign: "right" as const, minWidth: 80 },
  th: { fontSize: 9, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" as const, letterSpacing: "0.06em", py: 0.5, borderBottom: "1px solid #e5e1db" },
  td: { fontSize: 11, py: "4px", borderBottom: "1px solid #f3f0ec" },
};

const Row: React.FC<{ label: string; value: string; bold?: boolean }> = ({ label, value, bold }) => (
  <Box sx={S.row}>
    <Typography sx={S.label}>{label}</Typography>
    <Typography sx={{ ...S.value, fontWeight: bold ? 700 : 500 }}>{value}</Typography>
  </Box>
);

const AmountRow: React.FC<{ label: string; amount: number; bold?: boolean; color?: string }> = ({ label, amount, bold, color }) => (
  <Box sx={S.row}>
    <Typography sx={{ ...S.label, fontWeight: bold ? 600 : 400, color: color ?? S.label.color }}>{label}</Typography>
    <Typography sx={{ ...S.amount, fontWeight: bold ? 700 : 600, color: color ?? S.amount.color }}>{fmtCAD(amount)}</Typography>
  </Box>
);

const SectionTitle: React.FC<{ children: string }> = ({ children }) => (
  <Typography sx={S.sectionTitle}>{children}</Typography>
);

/* ═══════════════════════════════════════════════════════════════════════════
   BOOKING PRINT VIEW
   ═══════════════════════════════════════════════════════════════════════════ */
const BookingPrintView = React.forwardRef<HTMLDivElement, Props>(({ booking: b }, ref) => {
  const guests = n(b.guestCount);
  const halls = b.halls ?? [];
  const payments = b.payments ?? [];
  const addons = b.addons ?? [];
  const vendorItems = b.vendorItems ?? b.vendor_items ?? [];
  const totalDeposits = payments.reduce((s: number, p: any) => s + n(p.amount), 0);
  const balance = n(b.grandTotal) - totalDeposits;
  const pc = b.primaryCustomer ?? b.primary_customer;
  const sc = b.secondaryCustomer ?? b.secondary_customer;
  const slotLabel = b.timeSlotLabel || SLOT_LABELS[b.timeSlot] || b.timeSlot;
  const breakdownSections = useMemo(() => buildBreakdownSections(b), [b]);

  const docType = b.status === "order" ? "Confirmed Order" : b.status === "invoice" ? "Invoice" : "Quote";

  return (
    <Box ref={ref} sx={S.page}>

      {/* ══════════ WATERMARK ══════════ */}
      <Box component="img" src={logoImg} alt="" sx={{
        position: "fixed", top: "40%", left: "50%", transform: "translate(-50%, -50%)",
        width: 400, opacity: 0.04, pointerEvents: "none", zIndex: 0,
      }} />

      {/* ══════════ HEADER ══════════ */}
      <Box sx={{ ...S.header, position: "relative", zIndex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box component="img" src={logoImg} alt="DCC" sx={{ width: 80, height: "auto" }} />
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0d1b2a", lineHeight: 1.2 }}>
              Durham Convention Centre
            </Typography>
            <Typography sx={{ fontSize: 9, color: "#6b7280", mt: 0.3 }}>
              280 Taunton Rd E, Whitby, ON L1R 0H4 &nbsp;·&nbsp; (905) 665-0088
            </Typography>
          </Box>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#c9a96e", textTransform: "uppercase", letterSpacing: "0.08em" }}>{docType}</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0d1b2a", mt: 0.3 }}>{b.bookingRef || `#${b.id}`}</Typography>
          <Typography sx={{ fontSize: 9, color: "#6b7280" }}>
            Created {b.created_at?.split("T")[0]}
          </Typography>
        </Box>
      </Box>

      {/* ══════════ EVENT DETAILS ══════════ */}
      <SectionTitle>Event Details</SectionTitle>
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 4, rowGap: 0.3 }}>
        <Row label="Event Name" value={b.banquetName || "—"} />
        <Row label="Event Type" value={b.eventType || "—"} />
        <Row label="Event Date" value={b.eventDate?.split("T")[0] || "—"} />
        <Row label="Time Slot" value={slotLabel} />
        <Row label="Hall(s)" value={halls.map((h: any) => h.hallLabel || h.hallKey).join(", ") || "—"} />
        <Row label="Capacity" value={halls.length > 0 ? `${halls[0].hallCapacity} guests` : "—"} />
        <Row label="Guests" value={`${guests}${n(b.childrenCount) > 0 ? ` + ${b.childrenCount} children` : ""}`} />
        <Row label="Agent" value={b.agentName || "—"} />
        {b.lcboLicence === "yes" && <Row label="LCBO" value="Required" />}
      </Box>

      {/* ══════════ CUSTOMER ══════════ */}
      <SectionTitle>Customer</SectionTitle>
      {(b.organizationName || pc?.organization) && (
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0d1b2a", mb: 0.5 }}>
          {b.organizationName || pc?.organization}
        </Typography>
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 4 }}>
        <Box>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>Primary Contact</Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 500 }}>
            {[pc?.firstName, pc?.lastName].filter(Boolean).join(" ") || "—"}
          </Typography>
          {pc?.phone1 && <Typography sx={{ fontSize: 10, color: "#6b7280" }}>{pc.phone1}</Typography>}
          {pc?.email1 && <Typography sx={{ fontSize: 10, color: "#6b7280" }}>{pc.email1}</Typography>}
        </Box>
        {sc && (sc.firstName || sc.lastName) && (
          <Box>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase" }}>Secondary Contact</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 500 }}>
              {[sc.firstName, sc.lastName].filter(Boolean).join(" ") || "—"}
            </Typography>
            {sc.phone1 && <Typography sx={{ fontSize: 10, color: "#6b7280" }}>{sc.phone1}</Typography>}
            {sc.email1 && <Typography sx={{ fontSize: 10, color: "#6b7280" }}>{sc.email1}</Typography>}
          </Box>
        )}
      </Box>

      {/* ══════════ SERVICES TABLE ══════════ */}
      <SectionTitle>Services & Line Items</SectionTitle>

      {/* Table header */}
      <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
        <Typography sx={S.th}>Description</Typography>
        <Typography sx={{ ...S.th, textAlign: "right" }}>Rate</Typography>
        <Typography sx={{ ...S.th, textAlign: "center" }}>Qty</Typography>
        <Typography sx={{ ...S.th, textAlign: "right" }}>Amount</Typography>
      </Box>

      {/* Hall */}
      {halls.length > 0 && (
        <>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#c9a96e", mt: 1.5, mb: 0.3 }}>HALL RENTAL</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
            <Typography sx={S.td}>{halls[0].hallLabel || halls[0].hallKey}</Typography>
            <Typography sx={{ ...S.td, textAlign: "right" }}>
              {n(b.hallFlatFee) > 0 ? `${fmtCAD(n(b.hallFlatFee))} + ${fmtCAD(n(b.hallPricePerPerson))}/pp` : `${fmtCAD(n(b.hallPricePerPerson))}/pp`}
            </Typography>
            <Typography sx={{ ...S.td, textAlign: "center" }}>{guests}</Typography>
            <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(n(b.hallSubtotal))}</Typography>
          </Box>
        </>
      )}

      {/* Food */}
      {n(b.foodSubtotal) > 0 && (
        <>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#c9a96e", mt: 1.5, mb: 0.3 }}>FOOD & MENU</Typography>
          {b.foodServiceOption && (
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
              <Typography sx={S.td}>{b.foodServiceOption}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right" }}>{fmtCAD(n(b.foodServicePricePerPerson))}/pp</Typography>
              <Typography sx={{ ...S.td, textAlign: "center" }}>{guests}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(n(b.foodServicePricePerPerson) * guests)}</Typography>
            </Box>
          )}
          {b.menuOption && (
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
              <Typography sx={S.td}>{b.menuOption}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right" }}>{fmtCAD(n(b.menuPricePerPerson))}/pp</Typography>
              <Typography sx={{ ...S.td, textAlign: "center" }}>{guests}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(n(b.menuPricePerPerson) * guests)}</Typography>
            </Box>
          )}
        </>
      )}

      {/* Bar */}
      {n(b.barSubtotal) > 0 && (
        <>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#c9a96e", mt: 1.5, mb: 0.3 }}>BAR SERVICE</Typography>
          {b.barServiceOption && (
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
              <Typography sx={S.td}>{b.barServiceOption}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right" }}></Typography>
              <Typography sx={{ ...S.td, textAlign: "center" }}></Typography>
              <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(n(b.barServiceAmount))}</Typography>
            </Box>
          )}
          {b.barPackageOption && (
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
              <Typography sx={S.td}>{b.barPackageOption}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right" }}>{fmtCAD(n(b.barPackagePricePerPerson))}/pp</Typography>
              <Typography sx={{ ...S.td, textAlign: "center" }}>{guests}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(n(b.barPackagePricePerPerson) * guests)}</Typography>
            </Box>
          )}
          {n(b.bartenderCount) > 0 && (
            <Box sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
              <Typography sx={S.td}>{b.bartenderCount} Bartender{n(b.bartenderCount) > 1 ? "s" : ""}{n(b.barHours) > 0 ? ` (${b.barHours} hrs)` : ""}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right" }}>{fmtCAD(n(b.bartenderAmount))}</Typography>
              <Typography sx={{ ...S.td, textAlign: "center" }}>{b.bartenderCount}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(n(b.bartenderCount) * n(b.bartenderAmount))}</Typography>
            </Box>
          )}
        </>
      )}

      {/* LED */}
      {(b.selectedLEDs ?? []).length > 0 && (
        <>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#c9a96e", mt: 1.5, mb: 0.3 }}>LED & AV</Typography>
          {(b.selectedLEDs as string[]).map((led: string) => (
            <Box key={led} sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
              <Typography sx={S.td}>{led}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right" }}></Typography>
              <Typography sx={{ ...S.td, textAlign: "center" }}>1</Typography>
              <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(led.includes("1") || led.includes("2") ? 3000 : 1800)}</Typography>
            </Box>
          ))}
        </>
      )}

      {/* Add-ons */}
      {addons.length > 0 && (
        <>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#c9a96e", mt: 1.5, mb: 0.3 }}>ADD-ONS</Typography>
          {addons.map((a: any, i: number) => (
            <Box key={i} sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
              <Typography sx={S.td}>{a.itemName}{a.category ? ` (${a.category})` : ""}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right" }}>{fmtCAD(n(a.unitPrice))}</Typography>
              <Typography sx={{ ...S.td, textAlign: "center" }}>{a.quantity}</Typography>
              <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(n(a.lineTotal || a.quantity * a.unitPrice))}</Typography>
            </Box>
          ))}
        </>
      )}

      {/* Vendor Services */}
      {vendorItems.length > 0 && (() => {
        const grouped = new Map<string, any[]>();
        vendorItems.forEach((v: any) => {
          const cat = v.service_name_snapshot || v.serviceName || "Vendor Services";
          if (!grouped.has(cat)) grouped.set(cat, []);
          grouped.get(cat)!.push(v);
        });
        return (
          <>
            {Array.from(grouped.entries()).map(([cat, items]) => (
              <React.Fragment key={cat}>
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: "#c9a96e", mt: 1.5, mb: 0.3 }}>{cat.toUpperCase()}</Typography>
                {items.map((v: any, i: number) => {
                  const name = v.item_name_snapshot || v.itemName || "Item";
                  const vendor = v.vendor_name_snapshot || v.vendorName || "";
                  const pt = v.pricing_type || v.pricingType || "per_event";
                  const rate = n(v.rate);
                  const qty = pt === "per_hour" ? n(v.hours) : n(v.quantity);
                  const total = n(v.final_price || v.finalPrice);
                  return (
                    <Box key={i} sx={{ display: "grid", gridTemplateColumns: "2fr 0.8fr 0.5fr 0.8fr", px: 0.5 }}>
                      <Typography sx={S.td}>
                        {name}{vendor ? ` — ${vendor}` : ""}
                        <Typography component="span" sx={{ fontSize: 9, color: "#9ca3af", ml: 0.5 }}>
                          ({pt.replace("per_", "/ ").replace("_", " ")})
                        </Typography>
                      </Typography>
                      <Typography sx={{ ...S.td, textAlign: "right" }}>{fmtCAD(rate)}</Typography>
                      <Typography sx={{ ...S.td, textAlign: "center" }}>{qty}</Typography>
                      <Typography sx={{ ...S.td, textAlign: "right", fontWeight: 600 }}>{fmtCAD(total)}</Typography>
                    </Box>
                  );
                })}
              </React.Fragment>
            ))}
          </>
        );
      })()}

      {/* ══════════ FINANCIAL SUMMARY (with breakdown) ══════════ */}
      <Box sx={{ mt: 3, pt: 2, borderTop: "2px solid #0d1b2a", pageBreakInside: "avoid" }}>
        <Box sx={{ maxWidth: 360, ml: "auto" }}>
          {breakdownSections.filter(s => s.total > 0).map(section => (
            <Box key={section.name} sx={{ mb: 0.5 }}>
              <AmountRow label={section.name} amount={section.total} bold />
              {section.breakdown.length > 1 && section.breakdown.map((item, i) => (
                <Box key={i} sx={{ display: "flex", justifyContent: "space-between", py: "2px", pl: 1.5 }}>
                  <Box>
                    <Typography sx={{ fontSize: 9.5, color: "#6b7280" }}>{item.label}</Typography>
                    {item.rate && item.qty && (
                      <Typography sx={{ fontSize: 8.5, color: "#b0aaa0" }}>
                        {fmtCAD(item.rate)} × {item.qty}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 9.5, color: "#6b7280", fontVariantNumeric: "tabular-nums" }}>{fmtCAD(item.total)}</Typography>
                </Box>
              ))}
            </Box>
          ))}

          {n(b.adjustment) !== 0 && (
            <AmountRow label={`Adjustment${b.adjustmentReason ? ` (${b.adjustmentReason})` : ""}`} amount={n(b.adjustment)} />
          )}
          {n(b.serviceChargePercent) > 0 && (
            <AmountRow label={`Service (${b.serviceChargePercent}%)`} amount={n(b.serviceChargeAmount)} />
          )}
          {n(b.gratuityPercent) > 0 && (
            <AmountRow label={`Gratuity (${b.gratuityPercent}%)`} amount={n(b.gratuityAmount)} />
          )}

          <Box sx={{ height: 1, bgcolor: "#e5e1db", my: 1 }} />
          <AmountRow label="Subtotal" amount={n(b.subtotal)} bold />
          <AmountRow label="HST (13%)" amount={n(b.hstAmount)} />

          <Box sx={{ mt: 1.5, py: 1.2, px: 1.5, borderRadius: "8px", bgcolor: "#f5ede4", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#c9a96e", textTransform: "uppercase", letterSpacing: "0.08em" }}>Grand Total</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0d1b2a", fontVariantNumeric: "tabular-nums" }}>{fmtCAD(n(b.grandTotal))}</Typography>
          </Box>
        </Box>
      </Box>

      {/* ══════════ PAYMENTS ══════════ */}
      {(payments.length > 0 || n(b.securityDepositAmount) > 0) && (
        <Box sx={{ mt: 2 }}>
          <SectionTitle>Deposits & Payments</SectionTitle>
          <Box sx={{ maxWidth: 320, ml: "auto" }}>
            {n(b.securityDepositAmount) > 0 && (
              <AmountRow label={`Security Deposit (${b.securityDepositMethod || "Cash"})`} amount={n(b.securityDepositAmount)} />
            )}
            {payments.filter((p: any) => n(p.amount) > 0).map((p: any, i: number) => (
              <AmountRow key={i} label={`Payment — ${p.method || "Cash"}${p.paymentDate ? ` · ${p.paymentDate?.split("T")[0]}` : ""}`} amount={n(p.amount)} />
            ))}
            {totalDeposits > 0 && (
              <>
                <Box sx={{ height: 1, bgcolor: "#e5e1db", my: 0.8 }} />
                <AmountRow label="Total Paid" amount={totalDeposits} bold />
                <AmountRow
                  label={balance > 0 ? "Balance Due" : balance < 0 ? "Credit / Overpaid" : "Paid in Full"}
                  amount={Math.abs(balance)} bold
                  color={balance > 0 ? "#9b2c2c" : balance < 0 ? "#1d4ed8" : "#2d6a35"}
                />
              </>
            )}
          </Box>
        </Box>
      )}

      {/* ══════════ NOTES ══════════ */}
      {(b.publicNotes || b.privateNotes) && (
        <Box sx={{ mt: 2, pageBreakInside: "avoid" }}>
          <SectionTitle>Notes</SectionTitle>
          {b.publicNotes && <Typography sx={{ fontSize: 11, color: "#1a1a1a", mb: 0.5 }}>{b.publicNotes}</Typography>}
          {b.privateNotes && <Typography sx={{ fontSize: 10, color: "#9ca3af", fontStyle: "italic" }}>Internal: {b.privateNotes}</Typography>}
        </Box>
      )}

      {/* ══════════ PREPARED BY ══════════ */}
      <Box sx={{ mt: 3, pt: 1.5, borderTop: "1px solid #e5e1db", pageBreakInside: "avoid" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: 9, color: "#9ca3af" }}>Prepared By</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: "#1a1a1a" }}>{b.agentName || "Admin"}</Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: 9, color: "#9ca3af" }}>Prepared Date</Typography>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: "#1a1a1a" }}>{b.created_at?.split("T")[0] || new Date().toISOString().split("T")[0]}</Typography>
          </Box>
        </Box>
      </Box>

      {/* ══════════ SIGNATURE SECTION ══════════ */}
      <Box sx={{ mt: 4, pageBreakInside: "avoid" }}>
        <SectionTitle>Signatures</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, mt: 2 }}>
          {/* Client */}
          <Box>
            <Box sx={{ height: 70 }} /> {/* signing space */}
            <Box sx={{ borderTop: "1px solid #1a1a1a", pt: 0.8 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#1a1a1a" }}>Client Signature</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 9, color: "#6b7280" }}>Name: ____________________________________</Typography>
              <Typography sx={{ fontSize: 9, color: "#6b7280", mt: 1.5 }}>Date: ____________________________________</Typography>
            </Box>
          </Box>
          {/* Authorized */}
          <Box>
            <Box sx={{ height: 70 }} />
            <Box sx={{ borderTop: "1px solid #1a1a1a", pt: 0.8 }}>
              <Typography sx={{ fontSize: 10, fontWeight: 600, color: "#1a1a1a" }}>Authorized Signature</Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 9, color: "#6b7280" }}>Name: Durham Convention Centre</Typography>
              <Typography sx={{ fontSize: 9, color: "#6b7280", mt: 1.5 }}>Date: ____________________________________</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ══════════ TERMS & CONDITIONS ══════════ */}
      <Box sx={{ mt: 4, pageBreakBefore: "always", pageBreakInside: "avoid" }}>
        <SectionTitle>Terms & Conditions</SectionTitle>
        <Box component="ol" sx={{ pl: 2, m: 0 }}>
          {DEFAULT_TERMS.map((term, i) => (
            <Box component="li" key={i} sx={{ fontSize: 10, color: "#6b7280", lineHeight: 1.7, mb: 0.3 }}>
              {term}
            </Box>
          ))}
        </Box>
        <Typography sx={{ fontSize: 9, color: "#9ca3af", mt: 2, fontStyle: "italic" }}>
          By signing above, the client acknowledges and agrees to the terms and conditions outlined in this document.
        </Typography>
      </Box>

      {/* ══════════ FOOTER ══════════ */}
      <Box sx={{ mt: 4, pt: 2, borderTop: "1px solid #e5e1db" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: 9, color: "#9ca3af" }}>Durham Convention Centre</Typography>
          <Typography sx={{ fontSize: 9, color: "#9ca3af", fontStyle: "italic" }}>Thank you for choosing us</Typography>
        </Box>
        <Typography sx={{ fontSize: 8, color: "#c9c3b8", textAlign: "center", mt: 1 }}>
          280 Taunton Rd E, Whitby, ON L1R 0H4 &nbsp;·&nbsp; (905) 665-0088 &nbsp;·&nbsp; info@durhamcc.ca
        </Typography>
      </Box>
    </Box>
  );
});

BookingPrintView.displayName = "BookingPrintView";
export default BookingPrintView;
